import {
    BorderStyle,
    IBorder,
    IFilterMap,
    IFontStyle,
    IImageElementAsset,
    IImageSettings,
    IPadding,
    IRadius,
    ISerializedInlineStyledText,
    ISerializedVersionedText,
    IShadow,
    ITextShadow,
    IVideoElementAsset,
    IWidgetCustomProperty,
    OneOfCustomPropertyDto,
    OneOfElementPropertyKeys,
    OneOfStatePropertyKeys,
    OneOfValues,
    OneOfValuesDto,
    RadiusType,
    propertyToUnitMap
} from '@studio/domain';
import {
    BorderDto,
    FeedDto,
    FilterDto,
    ImageAssetDataDto,
    ImageSettingsDto,
    PaddingDto,
    RadiusDto,
    ShadowDto,
    TextShadowDto,
    VideoAssetDataDto,
    VideoSettingsDto
} from '@studio/domain/api/sapi';
import { cloneDeep } from '@studio/utils/clone';
import { handleError } from '@studio/utils/errors';
import { decimal, isNumber } from '@studio/utils/utils';
import { Color } from '../color';
import { parseColor, toRGBA } from '../color.utils';
import { IVideoSettings, PlaybackRate, VideoSizeMode } from '../elements/video/video';
import { deserializePropertyValue } from './property-value-serializer';
import { serializeNumber, serializeNumberOrFormula } from './serialization.utils';
import { deserializeWidgetProperty, convertWidgetPropertyToDto } from './widget-serializer';

const allowedFormulaProperties = ['x', 'y', 'rotationX', 'rotationY', 'rotationZ'];

const highPrecisionProperties = ['rotationX', 'rotationY', 'rotationZ'];

export function convertPropertyToDto<Value extends OneOfValues>(
    property: OneOfElementPropertyKeys,
    value: Value
): OneOfValuesDto {
    const unit = propertyToUnitMap[property];

    if (unit) {
        switch (unit) {
            case 'enum':
            case 'string':
                return value as string;
            case 'integer':
                return value as number;
            case 'number':
                return serializeNumberOrFormula(
                    value as number,
                    formulaAllowed(property),
                    isHighPrecision(property)
                );
            case 'boolean':
                return value as boolean;
            case 'Shadow[]':
                return convertShadowsToDtos(value as IShadow[]);
            case 'FilterMap':
                return convertFiltersToDto(value as IFilterMap);
            case 'TextShadow[]':
                return convertTextShadowsToDtos(value as ITextShadow[]);
            case 'Radius':
                return convertRadiusToDto(value as IRadius);
            case 'Border':
                return convertBorderToDto(value as IBorder);
            case 'Color':
                return value instanceof Color ? (value.toString() as string) : undefined;
            case 'Padding':
                return convertPaddingToDto(value as IPadding);
            case 'FontStyle':
                return value && (value as unknown as IFontStyle).id;
            case 'Feed':
                return value as FeedDto;
            case 'inline-styled-text':
                return value as ISerializedInlineStyledText;
            case 'text':
                return value as ISerializedVersionedText;
            case 'ImageAsset':
                delete (value as IImageElementAsset).__loading;
                return value as unknown as ImageAssetDataDto;
            case 'ImageSettings':
                return convertImageSettingsToDto(value as IImageSettings);
            case 'CustomProperty[]':
                return (value as IWidgetCustomProperty[]).map(v => convertWidgetPropertyToDto(v));
            case 'VideoSettings':
                return convertVideoSettingsToDto(value as IVideoSettings);
        }
    }

    throw new Error(`Could not serialize property "${property}:${unit}"`);
}

function formulaAllowed(property: OneOfElementPropertyKeys): boolean {
    return allowedFormulaProperties.some(p => p === property);
}

function isHighPrecision(property: OneOfElementPropertyKeys): boolean {
    return highPrecisionProperties.some(p => p === property);
}

function convertBorderToDto(border: IBorder): BorderDto {
    return {
        thickness: decimal(border.thickness),
        style: border.style,
        color: border.color.toString()
    };
}

function convertPaddingToDto(padding: IPadding): PaddingDto {
    return {
        top: decimal(padding.top),
        left: decimal(padding.left),
        right: decimal(padding.right),
        bottom: decimal(padding.bottom)
    };
}

function convertShadowsToDtos(value: IShadow[]): ShadowDto[] {
    return (
        value &&
        value.map(s => {
            let color: string;
            if (s.color instanceof Color) {
                color = toRGBA(s.color);
            } else {
                handleError('Shadow color is not an instance of Color', {
                    contexts: { color: s.color }
                });
                color = 'rgba(0,0,0,1)'; // default to black if we somehow do not have a color
            }
            return {
                offsetX: decimal(s.offsetX),
                offsetY: decimal(s.offsetY),
                blur: decimal(s.blur),
                spread: decimal(s.spread),
                color
            };
        })
    );
}

export function convertTextShadowsToDtos(value: ITextShadow[]): TextShadowDto[] {
    return (
        value &&
        value.map(s => {
            let color: string;
            if (s.color instanceof Color) {
                color = toRGBA(s.color);
            } else {
                handleError('Text shadow color is not an instance of Color', {
                    contexts: { color: s.color }
                });
                color = 'rgba(0,0,0,1)'; // default to black if we somehow do not have a color
            }

            return {
                offsetX: decimal(s.offsetX) || 0,
                offsetY: decimal(s.offsetY) || 0,
                blur: decimal(s.blur) || 0,
                color
            };
        })
    );
}

export function convertToNewRadius(value: number): IRadius {
    return {
        type: RadiusType.Joint,
        topLeft: value,
        topRight: value,
        bottomRight: value,
        bottomLeft: value
    };
}

function convertImageSettingsToDto(imageSettings: IImageSettings): ImageSettingsDto {
    return {
        ...imageSettings,
        x: serializeNumber(imageSettings.x, true),
        y: serializeNumber(imageSettings.y, true)
    };
}

function convertFiltersToDto(value: IFilterMap): FilterDto {
    return {
        ...value
    };
}

function convertRadiusToDto(value: IRadius): RadiusDto {
    return {
        type: value.type || RadiusType.Joint,
        topLeft: value.topLeft || 0,
        topRight: value.topRight || 0,
        bottomRight: value.bottomRight || 0,
        bottomLeft: value.bottomLeft || 0
    };
}

export function convertImageAssetToDto(
    imageAsset: IImageElementAsset | undefined
): ImageAssetDataDto | undefined {
    if (!imageAsset) {
        return;
    }

    return {
        id: imageAsset.id,
        src: imageAsset.url,
        width: Math.round(imageAsset.width),
        height: Math.round(imageAsset.height)
    };
}

export function convertVideoAssetToDto(
    videoAsset: IVideoElementAsset | undefined
): VideoAssetDataDto | undefined {
    if (!videoAsset) {
        return;
    }

    return {
        id: videoAsset.id,
        name: videoAsset.name,
        url: videoAsset.url,
        width: Math.round(videoAsset.width),
        height: Math.round(videoAsset.height),
        fileSize: videoAsset.fileSize > 1 ? videoAsset.fileSize : 1
    };
}

export function convertVideoSettingsToDto(videoSettings: IVideoSettings): VideoSettingsDto {
    const playbackButton = videoSettings.playbackButton;
    return {
        ...videoSettings,
        volume: videoSettings.volume || 0,
        playbackButton: {
            enabled: playbackButton.enabled,
            size: playbackButton.size,
            color: videoSettings.playbackButton.color.toString()
        }
    };
}

export function deserializeProperty(
    property: OneOfElementPropertyKeys | OneOfStatePropertyKeys,
    value: OneOfValuesDto | null | undefined,
    optional: boolean = false
): OneOfValues | undefined | any {
    if (value === null || value === undefined) {
        if (!optional) {
            throw new Error(`No value provided for property ${property}`);
        }
        return undefined;
    }

    const unit = propertyToUnitMap[property];

    switch (unit) {
        case 'number':
        case 'integer':
            if (typeof value === 'string' && !isNaN(value as any)) {
                return +value;
            }
            return value;
        case 'enum':
        case 'string':
        case 'boolean':
        case 'formula':
        case 'padding':
            return value;
        case 'Radius':
            return deserializeRadius(value as RadiusDto);
        case 'Color':
            return parseColor(value as string);
        case 'Border':
            return deserializeBorder(value as BorderDto);
        case 'Shadow[]':
            return deserializeShadow(value as ShadowDto[]);
        case 'FilterMap':
            return deserializeFilter(value as FilterDto);
        case 'TextShadow[]':
            return deserializeTextShadow(value as TextShadowDto[]);
        case 'Padding':
            return { ...(value as PaddingDto) };
        case 'Feed':
            return { ...(value as FeedDto) };
        case 'inline-styled-text':
            return { ...(value as ISerializedInlineStyledText) };
        case 'text':
            return { ...(value as ISerializedVersionedText) };
        case 'ImageAsset':
            return { ...(value as ImageAssetDataDto) };
        case 'ImageSettings':
            return { ...(value as ImageSettingsDto) };
        case 'CustomProperty[]':
            return (value as OneOfCustomPropertyDto[]).map(v => deserializeWidgetProperty(v));
        case 'VideoAsset':
            return deserializePropertyValue(property, value) as VideoAssetDataDto;
        case 'VideoSettings':
            const videoSettings = deserializePropertyValue(property, value) as VideoSettingsDto;
            return deserializeVideoSettings(videoSettings);
        default:
            throw new Error(`Unknown type of '${property}'`);
    }
}

function deserializeVideoSettings(videoSettings: VideoSettingsDto): IVideoSettings {
    const playbackButton = videoSettings.playbackButton;
    return {
        ...videoSettings,
        playbackRate: videoSettings.playbackRate as PlaybackRate,
        sizeMode: videoSettings.sizeMode as VideoSizeMode,
        playbackButton: {
            enabled: playbackButton.enabled,
            size: playbackButton.size,
            color: parseColor(videoSettings.playbackButton.color)
        }
    };
}

function getBorderStyle(borderDto: BorderDto): BorderStyle {
    const style: BorderStyle = borderDto.style;

    // Fix corrupt data in DB (seen cases of "undefined")
    if (style === 'dashed' || style === 'solid' || style === 'dotted') {
        return style;
    }
    handleError('Invalid border style', { contexts: { border: borderDto } });
    return 'solid';
}

function deserializeRadius(radius: RadiusDto | number): IRadius {
    if (isNumber(radius)) {
        return convertToNewRadius(radius);
    }

    return {
        ...radius,
        type: radius.type as RadiusType
    };
}

function deserializeBorder(borderDto: BorderDto): IBorder {
    const style = getBorderStyle(borderDto);

    return (
        borderDto && {
            thickness: borderDto.thickness,
            style, // Both might exist in library / documents
            color: parseColor(borderDto.color)
        }
    );
}

function deserializeShadow(shadowDto?: ShadowDto[]): IShadow[] | undefined {
    if (!shadowDto) {
        return;
    }

    return shadowDto.map(s => {
        return {
            offsetX: s.offsetX,
            offsetY: s.offsetY,
            blur: s.blur,
            spread: s.spread,
            color: parseColor(s.color)
        } satisfies IShadow;
    });
}

function deserializeFilter(filterDto: FilterDto = {}): IFilterMap | undefined {
    return cloneDeep(filterDto);
}

function deserializeTextShadow(textShadowDto?: TextShadowDto[]): ITextShadow[] | undefined {
    if (!textShadowDto) {
        return;
    }

    return textShadowDto.map(s => {
        return {
            offsetX: s.offsetX,
            offsetY: s.offsetY,
            blur: s.blur,
            color: parseColor(s.color)
        } satisfies ITextShadow;
    });
}
